import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, forkJoin, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { SERVICE_CONFIG } from '../constants';
import { RequesterService } from './requester.service';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ROUTING } from '../constants/routing.constants';
import { TOKEN } from '../constants/token.constant';
import { languageContext, languageStorageContext } from '../enums';
import { hasToken } from '../utils/auth-utils';
import { getBrowserLanguage, toLowerCaseLanguages } from '../utils/language-utils';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate = inject(TranslateService);
  private readonly requesterService = inject(RequesterService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  readonly languageSet = signal(false);
  readonly languageSet$ = toObservable(this.languageSet);
  readonly currentLang = signal<string | null>(null);

  private _languageContextPath = 'method';
  private readonly availableLanguagesCache = signal<string[] | null>(null);
  private readonly languageLocalChangedSubject = new ReplaySubject<string>(1);
  readonly languageLocalChanged = this.languageLocalChangedSubject
    .asObservable()
    .pipe(distinctUntilChanged());
  private readonly languageBackendChangedSubject = new Subject<string>();
  readonly languageBackendChanged$ = this.languageBackendChangedSubject.asObservable();

  private readonly contextSubject = new BehaviorSubject<
    languageContext.IDHUB | languageContext.EXTERNAL | null
  >(null);

  readonly context$ = this.contextSubject.asObservable().pipe(
    filter(
      (context): context is languageContext.IDHUB | languageContext.EXTERNAL => context !== null
    ),
    distinctUntilChanged()
  );
  private static readonly PATH_MAP: ReadonlyArray<[string, string]> = [
    [ROUTING.PHONE_NUMBER_OTP, 'phone-verifier-otp'],
    [ROUTING.PHONE_NUMBER, 'phone-verifier'],
    [ROUTING.M_OBYWATEL, 'mobywatel'],
    [ROUTING.EDO_APP_EID, 'edo'],
    [ROUTING.FREE_SIGNATURES_COMMERCIAL, 'free-signatures'],
    [ROUTING.EDO_APP_VERIFICATION, 'edo'],
  ];

  initLanguageContext({
    path,
    currentLanguage,
    allowedLanguages,
    context,
  }: {
    path: string;
    currentLanguage?: string;
    allowedLanguages?: string[];
    context: languageContext.IDHUB | languageContext.EXTERNAL;
  }): void {
    const storageKey = this.getLanguageStorageKey(context);
    const stored = this.getStoredLanguageData(storageKey);
    const browserLang = getBrowserLanguage();

    const langToUse = currentLanguage || stored?.currentLanguage || browserLang;

    const listToUse =
      allowedLanguages && allowedLanguages.length > 0
        ? allowedLanguages
        : stored?.languagesList?.length
          ? stored.languagesList
          : [langToUse];

    const source =
      allowedLanguages && allowedLanguages.length > 0
        ? 'backend.allowedLanguages'
        : stored?.languagesList?.length
          ? 'stored.languagesList'
          : 'fallback.singleLanguage';

    this._languageContextPath = path;
    this.availableLanguagesCache.set(null);

    this.storeLanguageData(langToUse, listToUse, storageKey);
    this.updateLanguageContext(context);

    this.setLanguage(langToUse)
      .pipe(
        take(1),
        switchMap(() =>
          hasToken(context === languageContext.IDHUB ? TOKEN.JWT : TOKEN.EXTERNAL_JWT)
            ? this.sendLanguage(langToUse)
            : of(null)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    if (context === languageContext.EXTERNAL) {
      this.checkExternalLanguageContext(langToUse, listToUse, storageKey);
    }
  }

  changeLanguage(lang: string): void {
    const normalizedLang = lang.toLowerCase();
    const stored = this.getStoredLanguageData();
    const updatedList = stored?.languagesList?.length ? stored.languagesList : [normalizedLang];

    this.setLanguage(normalizedLang)
      .pipe(
        take(1),
        switchMap(() => (hasToken(TOKEN.JWT) ? this.sendLanguage(normalizedLang) : of(null))),
        tap(() => this.languageBackendChangedSubject.next(normalizedLang))
      )
      .subscribe();

    this.storeLanguageData(normalizedLang, updatedList);
  }

  getStoredLanguageData(
    storageKey?: string
  ): { currentLanguage: string; languagesList: string[] } | null {
    try {
      const key = this.resolveStorageKey(storageKey);
      const data = localStorage.getItem(key);
      const parsed = data ? JSON.parse(data) : null;

      if (parsed?.currentLanguage && Array.isArray(parsed.languagesList)) {
        return parsed;
      }

      return null;
    } catch {
      return null;
    }
  }

  setLanguageFromStorage(context: languageContext): void {
    this.updateLanguageContext(context);
    const storageKey = this.getLanguageStorageKey(context);
    const stored = this.getStoredLanguageData(storageKey);
    if (stored?.currentLanguage && stored.languagesList?.length) {
      this.setLanguage(stored.currentLanguage)
        .pipe(take(1))
        .subscribe(() => {
          this.languageLocalChangedSubject.next(stored.currentLanguage.toLowerCase());
        });
    }
  }

  setLanguage(lang?: string): Observable<void> {
    const language = (lang || getBrowserLanguage()).toLowerCase();

    if (this.currentLang()?.toLowerCase() === language) {
      return of(void 0);
    }

    this.currentLang.set(language);

    return this.translate.use(language).pipe(
      tap(() => {
        this.languageSet.set(true);
        this.languageLocalChangedSubject.next(language);
      }),
      map(() => void 0)
    );
  }

  filterLanguagesWithAvailableFiles(languagesFromBackend: string[]): Observable<string[]> {
    const cached = this.availableLanguagesCache();
    if (cached !== null) {
      return of(cached);
    }

    const checks = languagesFromBackend.map((lang) =>
      this.http.get(`/assets/i18n/${lang}.json`).pipe(
        map(() => lang),
        catchError(() => of(null))
      )
    );

    return forkJoin(checks).pipe(
      map((results) => results.filter((lang): lang is string => !!lang)),
      tap((result) => this.availableLanguagesCache.set(result))
    );
  }

  getTranslatedLanguageItems(languages: string[]): Observable<{ key: string; value: string }[]> {
    const requests = languages.map((lang) =>
      this.translate.get(`common.languages.${lang}`).pipe(
        map((translated) => ({
          key: translated || lang,
          value: lang,
        }))
      )
    );
    return forkJoin(requests);
  }

  private updateLanguageContext(context: languageContext) {
    this.contextSubject.next(context);
  }

  private storeLanguageData(
    currentLanguage: string,
    languagesList: string[],
    storageKey?: string
  ): void {
    const key = this.resolveStorageKey(storageKey);
    const data = {
      currentLanguage: currentLanguage.toLowerCase(),
      languagesList: toLowerCaseLanguages(languagesList),
    };
    localStorage.setItem(key, JSON.stringify(data));
  }

  private checkExternalLanguageContext(langToUse: string, listToUse: any, storageKey: string) {
    const idhData = this.getStoredLanguageData(languageStorageContext.IDHUB);
    if (
      idhData?.currentLanguage &&
      listToUse.includes(idhData.currentLanguage) &&
      idhData.currentLanguage.toLowerCase() !== langToUse.toLowerCase()
    ) {
      this.storeLanguageData(idhData.currentLanguage, listToUse, storageKey);
      this.setLanguage(idhData.currentLanguage)
        .pipe(
          take(1),
          switchMap(() => this.sendLanguage(idhData.currentLanguage)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  private resolveStorageKey(storageKey?: string): string {
    return storageKey || this.getLanguageStorageKey();
  }

  private getCurrentContext(): languageContext.IDHUB | languageContext.EXTERNAL {
    const context = this.contextSubject.getValue();

    if (context === null) {
      throw new Error('Language context has not been initialized yet.');
    }

    return context;
  }

  private getLanguageStorageKey(context = this.getCurrentContext()): string {
    return context === languageContext.EXTERNAL
      ? languageStorageContext.EXTERNAL
      : languageStorageContext.IDHUB;
  }

  private sendLanguage(language: string): Observable<any> {
    let path = this._languageContextPath;
    const lang = language.toUpperCase();

    if (!path || path.includes('method')) {
      path = 'scenario';
      return this.requesterService.request(SERVICE_CONFIG.languages.setLanguage, { path, lang });
    }

    const matched = LanguageService.PATH_MAP.find(([key]) => path.includes(key));
    if (matched) {
      path = matched[1];
    }

    return this.requesterService.request(SERVICE_CONFIG.languages.setLanguageExternal, {
      path,
      lang,
    });
  }
}
